import { useState } from 'react';
import { useSensor, useSensors, PointerSensor, KeyboardSensor } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { Site, Page, Blog, BlogStats, PageLayout } from '../../schemas/validation';

interface BlogData {
  blog: Blog;
  stats: BlogStats;
}

export const useSiteManagerState = () => {
  // Core state
  const [site, setSite] = useState<Site | null>(null);
  const [pages, setPages] = useState<Page[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Page modals state
  const [showCreatePageModal, setShowCreatePageModal] = useState(false);
  const [isCreatingPage, setIsCreatingPage] = useState(false);
  const [showEditPageModal, setShowEditPageModal] = useState(false);
  const [pageToEdit, setPageToEdit] = useState<Page | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pageToDelete, setPageToDelete] = useState<Page | null>(null);
  const [isDeletingPage, setIsDeletingPage] = useState(false);
  const [canCreatePage, setCanCreatePage] = useState(true);

  // Drag and drop state
  const [isReordering, setIsReordering] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Template and publish state
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  // Toast state
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Theme and settings state
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [showFaviconModal, setShowFaviconModal] = useState(false);
  const [showResponsivePreview, setShowResponsivePreview] = useState(false);
  const [showSeoModal, setShowSeoModal] = useState(false);
  const [showSEOOverview, setShowSEOOverview] = useState(false);

  // Blog state
  const [blogData, setBlogData] = useState<BlogData | null>(null);
  const [isBlogLoading, setIsBlogLoading] = useState(false);
  const [isInstallingBlog, setIsInstallingBlog] = useState(false);
  const [isUninstallingBlog, setIsUninstallingBlog] = useState(false);
  const [showBlogUninstallConfirm, setShowBlogUninstallConfirm] = useState(false);

  // Layouts state
  const [layouts, setLayouts] = useState<PageLayout[]>([]);
  const [isLayoutsLoading, setIsLayoutsLoading] = useState(false);
  const [layoutToDelete, setLayoutToDelete] = useState<PageLayout | null>(null);
  const [showLayoutDeleteConfirm, setShowLayoutDeleteConfirm] = useState(false);
  const [isDeletingLayout, setIsDeletingLayout] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Toast helper
  const showToast = (message: string, type: 'success' | 'error' = 'success', duration = 3000) => {
    setSuccessMessage(message);
    setToastType(type);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), duration);
  };

  // Get the active page for drag overlay
  const activePage = activeId ? (pages.find(page => page.id === activeId) ?? null) : null;

  return {
    // Core state
    site,
    setSite,
    pages,
    setPages,
    isLoading,
    setIsLoading,

    // Page modals
    showCreatePageModal,
    setShowCreatePageModal,
    isCreatingPage,
    setIsCreatingPage,
    showEditPageModal,
    setShowEditPageModal,
    pageToEdit,
    setPageToEdit,
    showDeleteModal,
    setShowDeleteModal,
    pageToDelete,
    setPageToDelete,
    isDeletingPage,
    setIsDeletingPage,
    canCreatePage,
    setCanCreatePage,

    // Drag and drop
    sensors,
    isReordering,
    setIsReordering,
    activeId,
    setActiveId,
    activePage,

    // Template and publish
    showSaveTemplateModal,
    setShowSaveTemplateModal,
    isSavingTemplate,
    setIsSavingTemplate,
    showPublishModal,
    setShowPublishModal,
    isPublishing,
    setIsPublishing,

    // Toast
    showSuccessToast,
    setShowSuccessToast,
    successMessage,
    toastType,
    showToast,

    // Theme and settings
    showThemeSelector,
    setShowThemeSelector,
    showFaviconModal,
    setShowFaviconModal,
    showResponsivePreview,
    setShowResponsivePreview,
    showSeoModal,
    setShowSeoModal,
    showSEOOverview,
    setShowSEOOverview,

    // Blog
    blogData,
    setBlogData,
    isBlogLoading,
    setIsBlogLoading,
    isInstallingBlog,
    setIsInstallingBlog,
    isUninstallingBlog,
    setIsUninstallingBlog,
    showBlogUninstallConfirm,
    setShowBlogUninstallConfirm,

    // Layouts
    layouts,
    setLayouts,
    isLayoutsLoading,
    setIsLayoutsLoading,
    layoutToDelete,
    setLayoutToDelete,
    showLayoutDeleteConfirm,
    setShowLayoutDeleteConfirm,
    isDeletingLayout,
    setIsDeletingLayout,
  };
};

export type SiteManagerState = ReturnType<typeof useSiteManagerState>;
